import React, { memo } from "react";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import { BiCopy } from "react-icons/bi";
import { FiEdit } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";
import PropTypes from "prop-types";

const ResponsiveReactGridLayout = WidthProvider(Responsive);
const calculateNextPosition = (layout, itemWidth = 3, itemHeight = 3, maxX = 16) => {
  // Find the maximum y position for existing items
  let maxY = 0;

  // Track grid occupancy
  const grid = {};

  layout.forEach((item) => {
    for (let i = 0; i < item.w; i++) {
      for (let j = 0; j < item.h; j++) {
        const posX = item.x + i;
        const posY = item.y + j;
        grid[`${posX},${posY}`] = true;
      }
    }
    maxY = Math.max(maxY, item.y + item.h);
  });

  // Find the next position where the new item can fit
  for (let y = 0; y <= maxY + 1; y++) {
    for (let x = 0; x <= maxX - itemWidth; x++) {
      let fits = true;

      // Check if the item can fit at this position
      for (let i = 0; i < itemWidth; i++) {
        for (let j = 0; j < itemHeight; j++) {
          if (grid[`${x + i},${y + j}`]) {
            fits = false;
            break;
          }
        }
        if (!fits) break;
      }

      if (fits) {
        return { x, y };
      }
    }
  }

  return { x: 0, y: maxY + 1 };
};

const DashboardLayout = memo(
  ({
    gridData,
    setGridData,
    setLayouts,
    layouts,
    mode,
    setEditItem = () => {},
    setComponentModal = () => {},
    selectedTiles = [],
    handleSelect = () => {},
  }) => {
    const { isDark } = useSelector((state) => state.theme);
    const { currentSite, currentSiteSelectedTiles } = useSelector((state) => state.dashboard);

    const onLayoutChange = (layout, data) => {
      setLayouts(data);
    };

    const handlerRemove = (id) => {
      const newData = gridData.filter((data) => data._id !== id);
      setGridData(newData);
      const newLayout = layouts?.lg?.filter((layout) => String(layout.i) !== String(id));
      setLayouts({ lg: newLayout });
    };

    const handlerUpdate = (item) => {
      setEditItem(item);
      setComponentModal(true);
    };

    const defaultLayout = {
      w: 3,
      h: 3,
      x: 0,
      y: 0,
      moved: false,
      static: false,
    };

    const handleCopy = (item) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You want to copy this tile?.",
        dangerMode: true,
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then((result) => {
        if (result.isConfirmed) {
          const _id = uuid();
          setGridData([
            ...gridData,
            {
              ...item,
              _id,
              chartTitle: item.chartTitle + " (copy)",
            },
          ]);

          const nextPosition = calculateNextPosition(layouts?.lg || []);

          setLayouts((prev) => ({
            lg: [...prev.lg, { ...defaultLayout, i: _id, ...nextPosition }],
          }));
        }
      });
    };

    const handleRemove = (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You want to remove this tile?.",
        dangerMode: true,
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then((result) => {
        if (result.isConfirmed) {
          handlerRemove(id);
        }
      });
    };

    return (
      <div>
        <ResponsiveReactGridLayout
          className={`layout ${mode !== "create" && (isDark ? "bg-dark" : "bg-white")} ${
            mode === "view" ? " client_view" : "create"
          }`}
          id="layout1"
          rowHeight={80}
          layouts={layouts}
          onLayoutChange={onLayoutChange}
          draggableCancel=".clickBtn"
          isDraggable={mode === "view" ? false : true}
          isResizable={mode === "view" ? false : true}
          // cols={{ lg: 16, md: 12, sm: 8, xs: 4, xxs: 2 }}
          // breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          breakpoints={{ lg: 900, md: 700, sm: 500, xs: 400, xxs: 300 }}
          cols={{ lg: 16, md: 12, sm: 8, xs: 4, xxs: 2 }}
        >
          {gridData?.map((i, idx) => {
            let item = { ...i };
            if (selectedTiles.length && selectedTiles.includes(item?._id)) {
              let items = [];
              currentSiteSelectedTiles?.forEach((tileId) => {
                const component = currentSite.items?.find((tile) => tile?._id === tileId);
                if (component) {
                  items.push(component);
                }
              });
              if (items.length) {
                item.locations = items[0]?.locations;
                item.operator = items[0]?.customOperator;
                item.xAxis = items[0]?.customXAxis;
              }
            }
            return (
              <div
                className={`dashboardTiles ${item.isBorderHidden ? "" : "withBorder"} ${
                  selectedTiles.includes(item?._id) ? "active" : ""
                }`}
                key={item._id}
                data-grid={layouts?.lg?.[idx] || defaultLayout}
                onDoubleClick={() => {
                  if (item.component !== "textBox") handleSelect(item?._id);
                }}
              >
                <div className="position-relative h-100 d-flex align-items-center">
                  {mode !== "view" ? (
                    <div
                      className="d-flex justify-content-end align-items-center position-absolute"
                      style={{ top: 0, right: 5, zIndex: 10 }}
                    >
                      <div className="clickBtn" onClick={() => handleCopy(item)}>
                        <BiCopy className="text-secondary me-2" />
                      </div>
                      <div className="clickBtn" onClick={() => handlerUpdate(item)}>
                        <FiEdit className="text-secondary me-1" />
                      </div>
                      <div className="clickBtn" onClick={() => handleRemove(item._id)}>
                        <MdDeleteForever className="fs-5 text-secondary" />
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  <div className="p-2 h-100 w-100">
                    <div className="ql">
                      <div className="ql-editor p-0 pb-1" style={{ textAlign: "center" }}>
                        {!item.isTitleHidden && <div dangerouslySetInnerHTML={{ __html: item.chartTitle }}></div>}
                      </div>
                    </div>
                    <img
                      src={`/img/chart/${item.component}.png`}
                      alt="img"
                      className="img-fluid w-100"
                      style={{ height: "calc(100% - 30px)" }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </ResponsiveReactGridLayout>
      </div>
    );
  },
);

export default DashboardLayout;

DashboardLayout.displayName = "DashboardLayout";

DashboardLayout.propTypes = {
  gridData: PropTypes.array,
  setGridData: PropTypes.func,
  setLayouts: PropTypes.func,
  layouts: PropTypes.array,
  mode: PropTypes.string,
  setEditItem: PropTypes.func,
  setComponentModal: PropTypes.func,
  selectedTiles: PropTypes.array,
  handleSelect: PropTypes.func,
  mainDashboard: PropTypes.bool,
};
